/**
 * Print styling for the scorecard.
 *
 * The goal is one page. A thirteen-column crosstab will not fit portrait, so
 * the sheet is forced to landscape and the table is shrunk hard: small type,
 * tight padding, and no wasted gutter. Beyond about fifteen columns it will
 * spill, which is the point at which the reader should narrow the parameter
 * selection before printing rather than have the page silently truncate.
 *
 * Classes are plain strings rather than makeStyles output because
 * react-to-print copies the markup into a separate document, where generated
 * class names are awkward to target.
 */

/** Hidden on screen, shown only in the printout. */
export const PRINT_ONLY_CLASS = "scorecard-print-only";

/** Shown on screen, suppressed in the printout. */
export const NO_PRINT_CLASS = "scorecard-no-print";

/** The element react-to-print clones. */
export const PRINT_ROOT_CLASS = "scorecard-print-root";

/** Title, context line, intro and legend — page one. */
export const PRINT_INTRO_CLASS = "scorecard-print-intro";

/** The crosstab — page two, to itself. */
export const PRINT_TABLE_CLASS = "scorecard-print-table";

export const PRINT_PAGE_STYLE = `
  @page {
    size: landscape;
    margin: 8mm;
  }

  @media print {
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .${PRINT_ONLY_CLASS} {
      display: block !important;
    }

    .${NO_PRINT_CLASS} {
      display: none !important;
    }

    /* Two pages, not three. The table carries break-inside: avoid, so it can
       never share a sheet with anything above it — left in document order that
       strands the title alone on page one and pushes the legend to page three.
       Ordering the intro and legend together as a cover page, then breaking,
       gives page one real content and page two the full width for the grid. */
    .${PRINT_ROOT_CLASS} {
      padding: 0;
      display: flex;
      flex-direction: column;
    }

    .${PRINT_ROOT_CLASS} .${PRINT_INTRO_CLASS} {
      order: 1;
      break-after: page;
      page-break-after: always;
    }

    .${PRINT_ROOT_CLASS} .${PRINT_TABLE_CLASS} {
      order: 2;
    }

    /* Sticky positioning confuses pagination and can leave the site column
       floating over the body; the printed table does not scroll anyway. */
    .${PRINT_ROOT_CLASS} th,
    .${PRINT_ROOT_CLASS} td {
      position: static !important;
    }

    /* Horizontal scroll must not clip the columns off the sheet. */
    .${PRINT_ROOT_CLASS} .MuiTableContainer-root {
      overflow: visible !important;
    }

    .${PRINT_ROOT_CLASS} table {
      width: 100% !important;
      min-width: 0 !important;
      table-layout: fixed;
      break-inside: avoid;
      page-break-inside: avoid;
      font-size: 7.5pt;
    }

    .${PRINT_ROOT_CLASS} th,
    .${PRINT_ROOT_CLASS} td {
      padding: 2px 3px !important;
      line-height: 1.15;
      word-break: break-word;
    }

    /* Keep the band fill: it is the whole point of the printout. */
    .${PRINT_ROOT_CLASS} td {
      border: 0.5pt solid #bbb;
    }

    /* The legend belongs on the same sheet, so make it compact and horizontal. */
    .${PRINT_ROOT_CLASS} ul {
      font-size: 6.5pt;
    }
  }
`;
